import { useState } from "react";
import { Plus, Trash, CaretDown, CaretUp } from "@phosphor-icons/react";

export const STATUT_STYLE = {
  constate: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  rapporte: "bg-sky-500/15 text-sky-300 border-sky-500/40",
  allegue: "bg-violet-500/15 text-violet-300 border-violet-500/40",
  a_confirmer: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  conteste: "bg-red-500/15 text-red-300 border-red-500/40",
  convenu: "bg-teal-500/15 text-teal-200 border-teal-500/40",
};
export const STATUT_LABEL = { constate: "Constaté", rapporte: "Rapporté", allegue: "Allégué", a_confirmer: "À confirmer", conteste: "Contesté", convenu: "Convenu" };

export function StatutBadge({ statut, className = "" }) {
  if (!statut) return null;
  return <span data-testid="statut-badge" className={`inline-block text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${STATUT_STYLE[statut] || "bg-zinc-800 text-zinc-300 border-zinc-700"} ${className}`}>{STATUT_LABEL[statut] || statut}</span>;
}

const inputCls = "w-full rounded-lg bg-zinc-950/70 border border-zinc-800 focus:border-amber-500/60 outline-none px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600";

function Field({ f, value, onChange, pieces }) {
  if (f.type === "statut") {
    return (
      <select data-testid={`field-${f.key}`} className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)}>
        <option value="">— statut —</option>
        {Object.keys(STATUT_LABEL).map((k) => <option key={k} value={k}>{STATUT_LABEL[k]}</option>)}
      </select>
    );
  }
  if (f.type === "select") {
    const opts = f.key === "piece" ? [["", "— aucune pièce —"], ...Object.entries(pieces || {}).map(([k, v]) => [k, v.label])] : f.options.map((o) => [o, o]);
    return (
      <select data-testid={`field-${f.key}`} className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)}>
        {opts.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
    );
  }
  if (f.type === "textarea") return <textarea data-testid={`field-${f.key}`} rows={f.rows || 3} className={inputCls} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
  if (f.type === "number") return <input data-testid={`field-${f.key}`} type="number" step="0.01" className={inputCls} value={value ?? ""} placeholder="non chiffré" onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} />;
  return <input data-testid={`field-${f.key}`} type={f.type === "date" ? "text" : "text"} className={inputCls} value={value ?? ""} placeholder={f.placeholder || ""} onChange={(e) => onChange(e.target.value)} />;
}

/** Liste éditable générique. items = objets (ou chaînes si schema = [{key:"_"}]). */
export function ListEditor({ items = [], schema, onChange, testid, pieces, titleKey, renderSummary, emptyLabel = "Aucune entrée." }) {
  const [open, setOpen] = useState(null);
  const primitive = schema.length === 1 && schema[0].key === "_";
  const get = (it, k) => (primitive ? it : it?.[k]);
  const update = (i, k, v) => onChange(items.map((it, j) => (j !== i ? it : primitive ? v : { ...it, [k]: v })));
  const remove = (i) => { if (window.confirm("Supprimer cette ligne ?")) onChange(items.filter((_, j) => j !== i)); };
  const add = () => { onChange([...items, primitive ? "" : Object.fromEntries(schema.map((f) => [f.key, f.type === "number" ? null : f.default ?? ""]))]); setOpen(items.length); };
  return (
    <div data-testid={testid} className="space-y-2">
      {items.length === 0 && <div className="text-sm text-zinc-500 italic">{emptyLabel}</div>}
      {items.map((it, i) => {
        const isOpen = open === i;
        const title = renderSummary ? renderSummary(it) : primitive ? it : it?.[titleKey || schema[0].key];
        return (
          <div key={i} className="rounded-xl border border-zinc-800 bg-zinc-900/50" data-testid={`${testid}-row-${i}`}>
            <div className="flex items-start gap-2 p-3">
              {!primitive && it?.statut !== undefined && <StatutBadge statut={it.statut} className="mt-1 shrink-0" />}
              <button type="button" onClick={() => setOpen(isOpen ? null : i)} className="flex-1 text-left text-sm text-zinc-200 leading-snug hover:text-white" data-testid={`${testid}-toggle-${i}`}>
                {title || <span className="text-zinc-500 italic">(vide)</span>}
              </button>
              <button type="button" onClick={() => setOpen(isOpen ? null : i)} className="text-zinc-500 hover:text-zinc-200" aria-label="Ouvrir">{isOpen ? <CaretUp /> : <CaretDown />}</button>
              <button type="button" onClick={() => remove(i)} className="text-zinc-600 hover:text-red-400" aria-label="Supprimer" data-testid={`${testid}-delete-${i}`}><Trash /></button>
            </div>
            {isOpen && (
              <div className="grid md:grid-cols-2 gap-3 px-3 pb-3">
                {schema.map((f) => (
                  <label key={f.key} className={`text-xs text-zinc-400 space-y-1 ${f.type === "textarea" || f.wide ? "md:col-span-2" : ""}`}>
                    {!primitive && <span className="block">{f.label}</span>}
                    <Field f={f} value={get(it, f.key)} onChange={(v) => update(i, f.key, v)} pieces={pieces} />
                  </label>
                ))}
              </div>
            )}
          </div>
        );
      })}
      <button type="button" onClick={add} data-testid={`${testid}-add`} className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-300 hover:text-amber-200 border border-amber-500/30 hover:border-amber-400/60 rounded-full px-3 py-1.5 transition-colors">
        <Plus weight="bold" /> Ajouter une ligne
      </button>
    </div>
  );
}
